/**
 * i18n Initialization
 *
 * Initializes react-i18next with all necessary plugins and configuration.
 * Import this file in your main.tsx to enable internationalization.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  NAMESPACES,
  DEFAULT_NS,
  DETECTION_OPTIONS,
  INTERPOLATION_CONFIG,
  BACKEND_CONFIG,
  DEBUG_MODE,
  REACT_OPTIONS,
  type SupportedLanguage,
} from './config';

// Initialize i18n
i18n
  // Load translations using HTTP backend
  .use(HttpBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize configuration
  .init(
    {
      // Language settings
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES.map((l: { code: string }) => l.code),

      // Namespace settings - preload all namespaces
      ns: NAMESPACES as unknown as string[],
      defaultNS: DEFAULT_NS,
      preload: SUPPORTED_LANGUAGES.map((l: { code: string }) => l.code),

      // Partial bundled languages for eager loading
      partialBundledLanguages: true,

      // Detection settings
      detection: DETECTION_OPTIONS,

      // Backend settings
      backend: BACKEND_CONFIG,

      // Interpolation settings
      interpolation: INTERPOLATION_CONFIG,

      // React settings - use suspense to wait for translations
      react: {
        ...REACT_OPTIONS,
        useSuspense: true,
      },

      // Debug mode - will log to console
      debug: DEBUG_MODE,

      // Loading behavior - use full locale codes (pt-BR, not pt)
      load: 'currentOnly',

      // Missing key handling
      saveMissing: DEBUG_MODE,
      saveMissingTo: 'all',
      missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
        if (DEBUG_MODE) {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] Missing translation: [${lngs.join(',')}][${ns}] ${key}`);
        }
      },

      // Parse options
      parseMissingKeyHandler: (key: string) => {
        if (DEBUG_MODE) {
          return `[MISSING: ${key}]`;
        }
        return key;
      },

      // Pluralization
      pluralSeparator: '_',
      nsSeparator: ':',
      keySeparator: '.',

      // Return behavior
      returnNull: false,
      returnEmptyString: true,
      returnObjects: false,
      joinArrays: false,
    },
    (err) => {
      if (err) {
        console.error('[i18n] Initialization error:', err);
      } else {
        document.documentElement.lang = i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE;
        console.log('[i18n] Initialized successfully');
        console.log('[i18n] Current language:', i18n.language);
        console.log('[i18n] Loaded resources:', Object.keys(i18n.store.data));
      }
    }
  );

i18n.on('languageChanged', (language) => {
  document.documentElement.lang = language || DEFAULT_LANGUAGE;
});

// Export the i18n instance
export default i18n;

// Re-export types and config for convenience
export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, NAMESPACES, DEFAULT_NS };
export type { SupportedLanguage };

// Utility function to change language
export const changeLanguage = async (lng: SupportedLanguage): Promise<void> => {
  console.log('[i18n] Changing language to:', lng);
  try {
    await i18n.changeLanguage(lng);
    console.log('[i18n] Language changed. Current:', i18n.language);
    console.log('[i18n] Loaded resources:', i18n.store.data);
  } catch (err) {
    console.error('[i18n] Error changing language:', err);
  }
};

// Utility function to get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language || DEFAULT_LANGUAGE) as SupportedLanguage;
};

// Utility function to check if language is supported
export const isSupportedLanguage = (lng: string): lng is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.some((l: { code: string }) => l.code === lng);
};

// Utility function to get language display name
export const getLanguageName = (code: string): string => {
  const lang = SUPPORTED_LANGUAGES.find((l: { code: string }) => l.code === code);
  return lang?.name || code;
};

// Utility function to get language flag
export const getLanguageFlag = (code: string): string => {
  const lang = SUPPORTED_LANGUAGES.find((l: { code: string }) => l.code === code);
  return lang?.flag || '🌐';
};
